import { createContext } from 'react';
import type { SupportGrant } from '../types';
import type { SupportSessionInfo } from './supportSession';

export interface SupportNotice {
  tone: 'info' | 'error';
  message: string;
}

/**
 * Staff support-session state (SC-2948), provided by AuthProvider beside the
 * auth context. Separate from `AuthContextValue` so the many surfaces (and
 * tests) that only read identity don't have to know about support mode.
 */
export interface SupportContextValue {
  // Non-null only while the session is inside a client org under a support
  // grant this tab entered — drives the banner.
  support: SupportSessionInfo | null;
  // A one-off message about a support transition that didn't go as planned
  // (couldn't enter, couldn't exit, the grant ended mid-session).
  notice: SupportNotice | null;
  dismissNotice: () => void;
  // Both resolve (never reject): failures land in `notice`.
  enterSupport: (grant: SupportGrant) => Promise<void>;
  exitSupport: () => Promise<void>;
  // Adopt a newer expiry for the CURRENT support org (a colleague re-granted,
  // which replaces the grant in place) so the expiry timer doesn't end a
  // session that is still live.
  syncSupportExpiry: (grant: SupportGrant) => void;
}

// Outside AuthProvider (isolated component tests) there is no support session:
// an inert value rather than a throw, so components that merely *may* show
// support UI render normally.
const INERT: SupportContextValue = {
  support: null,
  notice: null,
  dismissNotice: () => {},
  enterSupport: () => Promise.resolve(),
  exitSupport: () => Promise.resolve(),
  syncSupportExpiry: () => {},
};

export const SupportContext = createContext<SupportContextValue>(INERT);
